import React from 'react';
import { Typography, Box, Paper, Chip } from '@mui/material';
import { TrendingUp, TrendingDown, TrendingFlat } from '@mui/icons-material';
import { formatSchoolName } from '../../utils/nameFormatter';

const PublicMapPopup = ({ school, schoolData }) => {
  // Get enrollment data from schoolData if available
  const currentEnrollment = schoolData?.current_enrollment || 0;
  const previousEnrollment = schoolData?.previous_enrollment || 0;
  const enrollmentChange = schoolData?.enrollment_change_pct || null;

  const getGovernanceDisplay = (governance) => {
    if (governance === 'charter') return 'Charter School';
    if (governance === 'private') return 'Private School';
    return 'District School';
  };

  const getGovernanceColor = (governance) => {
    if (governance === 'charter') return 'secondary';
    if (governance === 'private') return 'warning';
    return 'primary';
  };

  const getTrendIcon = () => {
    if (enrollmentChange === null) return null;
    if (Math.abs(enrollmentChange) < 5) return <TrendingFlat sx={{ fontSize: 18, ml: 0.5 }} />;
    return enrollmentChange > 0 ? 
      <TrendingUp sx={{ fontSize: 18, ml: 0.5 }} /> : 
      <TrendingDown sx={{ fontSize: 18, ml: 0.5 }} />;
  };

  const getTrendColor = () => {
    if (enrollmentChange === null) return 'text.secondary';
    if (Math.abs(enrollmentChange) < 5) return 'text.secondary';
    return enrollmentChange > 0 ? 'success.main' : 'error.main';
  };

  return (
    <Paper 
      elevation={3} 
      sx={{ 
        p: 2, 
        minWidth: 280,
        maxWidth: 320
      }}
    >
      {/* School Name */}
      <Typography 
        variant="h6" 
        sx={{ 
          fontWeight: 600, 
          mb: 1,
          fontSize: '1.1rem'
        }}
      >
        {formatSchoolName(school.name)}
      </Typography>

      {/* Governance Type */}
      <Chip
        label={getGovernanceDisplay(school.governance || 'district')}
        color={getGovernanceColor(school.governance)}
        size="small"
        sx={{ mb: 2 }}
      />

      {/* Grade Range */}
      {school.grade_range && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Grades: {school.grade_range}
        </Typography>
      )}

      {/* Current Enrollment */}
      <Box sx={{ mb: 1.5 }}>
        <Typography variant="caption" color="text.secondary" display="block">
          Current Enrollment (2024-25)
        </Typography>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          {currentEnrollment > 0 ? currentEnrollment.toLocaleString() : 'No Data'}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Overlapping grades only
        </Typography>
      </Box>

      {/* Enrollment Change */}
      {enrollmentChange !== null && previousEnrollment > 0 && (
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center',
            pt: 1.5,
            borderTop: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" color="text.secondary" display="block">
              Change Since 2019-20
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography 
                variant="body1" 
                sx={{ 
                  fontWeight: 600,
                  color: getTrendColor()
                }}
              >
                {enrollmentChange > 0 ? '+' : ''}{(enrollmentChange ?? 0).toFixed(0)}%
              </Typography>
              <Box sx={{ color: getTrendColor() }}>
                {getTrendIcon()}
              </Box>
            </Box>
          </Box>
        </Box>
      )}

      {enrollmentChange === null && (
        <Box 
          sx={{ 
            pt: 1.5,
            borderTop: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Typography variant="caption" color="text.secondary" display="block">
            Change Since 2019-20
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            New/Newer School
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default PublicMapPopup;

